import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Rocket, Users, DollarSign, Heart } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { upgradeToCreator } from '@/lib/firebase/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface BecomeCreatorModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function BecomeCreatorModal({ isOpen, onClose }: BecomeCreatorModalProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const handleBecomeCreator = async () => {
        if (!user) return;

        setIsLoading(true);
        try {
            await upgradeToCreator(user.uid);
            toast.success('Welcome to Creator mode! Redirecting to creator dashboard...');
            onClose();
            setTimeout(() => {
                navigate('/creator');
            }, 1500);
        } catch (error) {
            toast.error('Failed to upgrade to creator account');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" aria-hidden="true"></div>
            <div className="relative z-10 bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Become a Creator</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <p className="text-gray-600">
                        Start your own mission and begin collecting funding from supporters. As a creator, you can:
                    </p>

                    <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                            <Rocket className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Create Your Mission</h3>
                                <p className="text-sm text-gray-600">Share your story and build your community</p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <Users className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Grow Your Audience</h3>
                                <p className="text-sm text-gray-600">Connect with supporters and followers</p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <DollarSign className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Receive Funding</h3>
                                <p className="text-sm text-gray-600">Collect monthly subscriptions and donations</p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <Heart className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Share Content</h3>
                                <p className="text-sm text-gray-600">Post updates and exclusive content for your supporters</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleBecomeCreator}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Upgrading...' : 'Become a Creator'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
